/**
 * Firebase Storage helpers
 * Placeholder module for file storage utilities
 */
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
} from 'firebase/storage'
import { getFirebaseStorage } from './firebase.js'

/**
 * Upload a file to storage
 * @param {string} path - Storage path
 * @param {File|Blob} file - File to upload
 * @returns {Promise<string>} Download URL
 */
export async function uploadFile(path, file) {
  const storage = getFirebaseStorage()
  const storageRef = ref(storage, path)
  await uploadBytes(storageRef, file)
  return getDownloadURL(storageRef)
}

/**
 * Upload a file with progress tracking
 * @param {string} path - Storage path
 * @param {File|Blob} file - File to upload
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<string>} Download URL
 */
export function uploadFileWithProgress(path, file, onProgress) {
  const storage = getFirebaseStorage()
  const storageRef = ref(storage, path)
  const uploadTask = uploadBytesResumable(storageRef, file)

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        if (onProgress) {
          onProgress(progress)
        }
      },
      (error) => reject(error),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref)
        resolve(url)
      }
    )
  })
}

/**
 * Get download URL for a file
 * @param {string} path - Storage path
 * @returns {Promise<string>}
 */
export async function getFileUrl(path) {
  const storage = getFirebaseStorage()
  const storageRef = ref(storage, path)
  return getDownloadURL(storageRef)
}

/**
 * Delete a file from storage
 * @param {string} path - Storage path
 * @returns {Promise}
 */
export async function deleteFile(path) {
  const storage = getFirebaseStorage()
  const storageRef = ref(storage, path)
  try {
    await deleteObject(storageRef)
  } catch (err) {
    // If file doesn't exist, treat as success (already deleted)
    if (err.code === 'storage/object-not-found') {
      return
    }
    throw err
  }
}

/**
 * List all files in a directory
 * @param {string} path - Storage path
 * @returns {Promise<Array>}
 */
export async function listFiles(path) {
  const storage = getFirebaseStorage()
  const listRef = ref(storage, path)
  const result = await listAll(listRef)
  return result.items.map((item) => item.fullPath)
}
